import {
  FormInput,
  PaymentProcessorEnum,
  PrimaryButton,
  getPaymentProcessorTitle,
} from '@systemeio/ui'
import HelpCenterLink from 'modules/profile/payment-gateways/components/help-center-link'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import { flutterwaveConnect } from 'modules/profile/payment-gateways/integrations/flutterwave/api'
import { PaymentFlutterwaveInterface } from 'modules/profile/payment-gateways/integrations/flutterwave/types'
import { getFlutterwaveHelpPageLink } from 'modules/profile/payment-gateways/integrations/flutterwave/utils'
import React, { useState } from 'react'
import CreateModal from 'shared/components/create-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'

interface FlutterwaveCreateModalProps {}

const defaultValues: PaymentFlutterwaveInterface = {
  apiEncryptionKey: '',
  apiKeySecret: '',
  apiPublicKey: '',
}

const defaultErrors: PaymentFlutterwaveInterface = {
  apiEncryptionKey: '',
  apiPublicKey: '',
  apiKeySecret: '',
}

const FlutterwaveCreateModal = ({}: FlutterwaveCreateModalProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [opened, setOpened] = useState(false)
  const [errors, setErrors] = useState<PaymentFlutterwaveInterface>(defaultErrors)

  const { mutatePaymentIntegrations } = useConnectedPaymentIntegrations()

  const onCreate = async (data: PaymentFlutterwaveInterface) => {
    await fetchWithErrors(async () => {
      await flutterwaveConnect(data)
      await mutatePaymentIntegrations()
    }, setErrors)
  }
  return (
    <>
      <PrimaryButton
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault()
          setOpened(true)
        }}
        className={'font-bold'}
      >
        {t('dashboard.settings.payment_gateways.connect')}
      </PrimaryButton>
      <CreateModal
        fullCaption={t('dashboard.settings.payment_gateways.connect_payment_title', {
          payment: getPaymentProcessorTitle(PaymentProcessorEnum.Flutterwave),
        })}
        opened={opened}
        onClose={() => setOpened(false)}
        defaultValues={defaultValues}
        needToast={false}
        creatable={[
          {
            field: 'apiPublicKey',
            onRender: (value, onChange) => (
              <FormInput
                key={'flutterwave-apiPublicKey-create'}
                label={t('dashboard.settings.payment-gateways.api_public_key')}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.apiPublicKey}
              />
            ),
          },
          {
            field: 'apiKeySecret',
            onRender: (value, onChange) => (
              <FormInput
                key={'flutterwave-apiKeySecret-create'}
                label={t('dashboard.settings.payment-gateways.api_secret_key')}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.apiKeySecret}
              />
            ),
          },
          {
            field: 'apiEncryptionKey',
            onRender: (value, onChange) => (
              <FormInput
                key={'flutterwave-apiEncryptionKey-create'}
                label={t('dashboard.settings.payment-gateways.api_encryption_key')}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.apiEncryptionKey}
              />
            ),
          },
        ]}
        additionalInfo={
          <HelpCenterLink
            link={getFlutterwaveHelpPageLink(user?.dashboardLocale)}
            integration={getPaymentProcessorTitle(PaymentProcessorEnum.Flutterwave)}
          />
        }
        onCreate={onCreate}
        requiredFields={['apiEncryptionKey', 'apiKeySecret', 'apiPublicKey']}
      />
    </>
  )
}

export default FlutterwaveCreateModal
